/**
 * API key handling for native-app WebView clients.
 *
 * When a request reaches the web with `?api_key=ev-super-...` (e.g. the
 * native AtomizerAR or Electronify app loads electronvisual.org with the
 * superuser key appended after a RevenueCat subscription is verified),
 * we capture it once, persist it in sessionStorage, and attach it as
 * `X-API-Key` to every backend request via the httpClient interceptor.
 *
 * The web's own credit / paywall logic is server-side: any request
 * carrying the matching SUPERUSER_API_KEY bypasses the credit check
 * entirely (`server/credits.py:require_credits`).
 *
 * sessionStorage (not localStorage) so the key dies when the WebView
 * tab does — a small belt-and-suspenders measure since the native app
 * controls when to inject in the first place.
 */

const STORAGE_KEY = "ev_api_key";
const SHELL_KEY = "ev_shell";
const URL_PARAM = "api_key";
const SHELL_PARAM = "shell";

export type ShellName =
  | "atomizer-ar"
  | "electronify"
  | "atomizer-mac"
  | "atomizer-vision"
  | "electronvisualized-mac"
  | null;

let cachedKey: string | null = null;
let cachedShell: ShellName = null;
let initialised = false;

function captureFromUrl(): { key: string | null; shell: ShellName } {
  let key: string | null = null;
  let shell: ShellName = null;
  try {
    const url = new URL(window.location.href);
    const fromUrl = url.searchParams.get(URL_PARAM);
    if (fromUrl) {
      sessionStorage.setItem(STORAGE_KEY, fromUrl);
      url.searchParams.delete(URL_PARAM);
      key = fromUrl;
    }
    const fromShell = url.searchParams.get(SHELL_PARAM);
    if (
      fromShell === "atomizer-ar" ||
      fromShell === "electronify" ||
      fromShell === "atomizer-mac" ||
      fromShell === "atomizer-vision" ||
      fromShell === "electronvisualized-mac"
    ) {
      sessionStorage.setItem(SHELL_KEY, fromShell);
      url.searchParams.delete(SHELL_PARAM);
      shell = fromShell;
    }
    if (fromUrl || fromShell) {
      // Strip params from the visible URL so a copy/paste doesn't
      // leak the key — values are now in sessionStorage.
      window.history.replaceState({}, "", url.toString());
    }
  } catch {}
  if (!key) {
    try { key = sessionStorage.getItem(STORAGE_KEY); } catch {}
  }
  if (!shell) {
    try {
      const stored = sessionStorage.getItem(SHELL_KEY);
      if (
        stored === "atomizer-ar" ||
        stored === "electronify" ||
        stored === "atomizer-mac" ||
        stored === "atomizer-vision" ||
        stored === "electronvisualized-mac"
      ) shell = stored;
    } catch {}
  }
  return { key, shell };
}

function init() {
  if (initialised) return;
  const { key, shell } = captureFromUrl();
  cachedKey = key;
  cachedShell = shell;
  initialised = true;
}

export function getApiKey(): string | null {
  init();
  return cachedKey;
}

export function getShell(): ShellName {
  init();
  return cachedShell;
}

/** True if the page is running inside a native-app WebView shell. */
export function isNativeWebView(): boolean {
  return getApiKey() !== null || getShell() !== null;
}

/** Only the AtomizerAR shell exposes WebXR AR features. */
export function isAtomizerArShell(): boolean {
  return getShell() === "atomizer-ar";
}

/** Resets the cached values — call from tests or after a manual sign-out. */
export function clearApiKey() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(SHELL_KEY);
  } catch {}
  cachedKey = null;
  cachedShell = null;
  initialised = false;
}

// Forward credit updates to native shells so their in-app chips refresh
// the moment a render is consumed, instead of waiting for the 30s poll.
// httpClient dispatches `ev-credits-update` after every credit-protected
// response (server echoes X-Credits-* headers).
if (typeof window !== "undefined") {
  window.addEventListener("ev-credits-update", (e: Event) => {
    const detail = (e as CustomEvent).detail;
    if (!detail) return;
    try {
      // iOS Atomizer AR (WKWebView)
      const ios = (window as any).webkit?.messageHandlers?.creditsUpdate;
      if (ios && typeof ios.postMessage === "function") {
        ios.postMessage(detail);
      }
      // Electronify Android (react-native-webview)
      const rn = (window as any).ReactNativeWebView;
      if (rn && typeof rn.postMessage === "function") {
        rn.postMessage(JSON.stringify({ type: "creditsUpdate", ...detail }));
      }
    } catch {}
  });
}
